import type {
  AccessoryOption,
  BikeVariant,
  QuotationLine,
  QuotationLineInput,
  QuotationLineType,
} from './api';
import { LINE_TYPE_LABEL } from './status';

/**
 * Pure edits on a quotation's lines. Money stays a decimal STRING throughout.
 * BIKE and ACCESSORY lines never carry a unit price: the server is the only
 * source of those, so a stale price on screen can never be sent back.
 */

export type FeeType = Exclude<QuotationLineType, 'BIKE' | 'ACCESSORY'>;

export const FEE_TYPES: readonly FeeType[] = ['INSURANCE', 'ROAD_TAX', 'REGISTRATION', 'SERVICE', 'OTHER'];

export function toLineInputs(lines: QuotationLine[]): QuotationLineInput[] {
  return lines.map((line): QuotationLineInput => {
    const base: QuotationLineInput = {
      type: line.type,
      description: line.description,
      qty: line.qty,
      discountAmount: line.discountAmount,
    };
    if (line.type === 'BIKE') {
      return line.itemCode === null ? base : { ...base, itemCode: line.itemCode };
    }
    if (line.type === 'ACCESSORY') {
      return line.skuUid === null ? base : { ...base, skuUid: line.skuUid };
    }
    return { ...base, unitPrice: line.unitPrice };
  });
}

export function withBike(lines: QuotationLineInput[], variant: BikeVariant): QuotationLineInput[] {
  const bike: QuotationLineInput = {
    type: 'BIKE',
    description: variant.description,
    qty: '1',
    itemCode: variant.itemCode,
  };
  const at = lines.findIndex((l) => l.type === 'BIKE');
  if (at === -1) return [bike, ...lines];
  return lines.map((l, i) => (i === at ? bike : l));
}

export function withAccessory(lines: QuotationLineInput[], part: AccessoryOption): QuotationLineInput[] {
  return [...lines, { type: 'ACCESSORY', description: part.name, qty: '1', skuUid: part.uid }];
}

export function withFee(
  lines: QuotationLineInput[],
  type: FeeType,
  amount: string,
  description?: string,
): QuotationLineInput[] {
  const typed = description?.trim() ?? '';
  return [
    ...lines,
    {
      type,
      // Stored as document data in English. When ms/zh land, decide whether the
      // default description should follow the customer's language (review M10).
      description: typed.length > 0 ? typed : (LINE_TYPE_LABEL[type] ?? type),
      qty: '1',
      unitPrice: amount,
    },
  ];
}

export function withQty(lines: QuotationLineInput[], index: number, qty: string): QuotationLineInput[] {
  return lines.map((l, i) => (i === index ? { ...l, qty } : l));
}

/** True when a BIKE or ACCESSORY line has no item code / sku: the server would refuse it. */
export function isUnkeyed(line: QuotationLineInput): boolean {
  if (line.type === 'BIKE') return line.itemCode === undefined;
  if (line.type === 'ACCESSORY') return line.skuUid === undefined;
  return false;
}

export function hasUnkeyed(lines: QuotationLineInput[]): boolean {
  return lines.some(isUnkeyed);
}

/** Compares decimal strings by value ('500' equals '500.00') without parsing to a number. */
export function sameDecimal(a: string, b: string): boolean {
  const norm = (v: string): string => {
    const [whole = '', fraction = ''] = v.trim().split('.');
    const w = whole.replace(/^0+(?=\d)/, '');
    const f = fraction.replace(/0+$/, '');
    const head = w === '' ? '0' : w;
    return f.length > 0 ? `${head}.${f}` : head;
  };
  return norm(a) === norm(b);
}

export function withDiscount(
  lines: QuotationLineInput[],
  index: number,
  amount: string,
): QuotationLineInput[] {
  return lines.map((l, i) => (i === index ? { ...l, discountAmount: amount } : l));
}

export function withoutLine(lines: QuotationLineInput[], index: number): QuotationLineInput[] {
  return lines.filter((_, i) => i !== index);
}

export interface PriceChange {
  description: string;
  from: string;
  to: string;
}

/**
 * BIKE and ACCESSORY lines whose AutoCount price moved between the lines the
 * advisor saw and the lines the server saved. A draft is re-priced from
 * AutoCount on every save; this is how that is made visible rather than silent.
 * Lines are matched on what they are (item code, part), so choosing a
 * different bike is not reported as a price change.
 */
export function priceChanges(previous: QuotationLine[], saved: QuotationLine[]): PriceChange[] {
  const key = (line: QuotationLine): string | null => {
    if (line.type === 'BIKE' && line.itemCode !== null) return `BIKE:${line.itemCode.toUpperCase()}`;
    if (line.type === 'ACCESSORY' && line.skuUid !== null) return `PART:${line.skuUid}`;
    return null;
  };
  const before = new Map<string, QuotationLine>();
  for (const line of previous) {
    const k = key(line);
    if (k !== null) before.set(k, line);
  }
  return saved.flatMap((line): PriceChange[] => {
    const k = key(line);
    const old = k === null ? undefined : before.get(k);
    if (old === undefined || sameDecimal(old.unitPrice, line.unitPrice)) return [];
    return [{ description: line.description, from: old.unitPrice, to: line.unitPrice }];
  });
}
